<!--
  CATFinalPage.vue
  -----------------
  Auteur       : Janfix - Jean-Philippe Rivière
  Projet       : WASPL – Plateforme d'évaluation numérique
  Description  : Composant de page finale pour les tests CAT (Computer Adaptive Testing)
  Licence      : AGPL-3.0 pour la communauté des développeurs et usage non commercial
                Propriétaire pour les utilisations professionnelles et commerciales
  Version      : 1.0.0
  Date         : 2025

  📌 Composant Vue 3 dédié à l'affichage de la page finale des tests adaptatifs
-->

<template>
  <div class="cat-final-page">
    <!-- Navbar -->
    <topnavbar 
      :testData="testData" 
      :answers="answers" 
      :stop-timer="stopTimer"
      @time-up="$emit('time-up')" 
      @toggle-test-map="() => {}" 
    />
    
    <!-- Contenu principal -->
    <div class="container wrapper">
      <!-- Message de fin principal -->
      <div class="completion-alert">
        <h4>🎉 {{ $t('Evaluation Completed') }}!</h4>
        <p>{{ $t('Your level has been determined based on your responses') }}.</p>
        <p>{{ $t('Estimated ability level') }}: <strong>{{ finalAbilityLevel.toFixed(2) }}</strong></p>
        <p>{{ $t('Questions answered') }}: <strong>{{ answeredQuestions.length }}</strong></p>
        <!-- Indicateur de sauvegarde dynamique -->
        <div class="saving-indicator" :class="savingStatus">
          <!-- En cours de sauvegarde -->
          <div v-if="savingStatus === 'saving'" class="saving-progress">
            <i class="fas fa-spinner fa-spin me-2"></i>{{ $t('Saving results process') }}...
          </div>
          
          <!-- Sauvegarde réussie -->
          <div v-else-if="savingStatus === 'success'" class="saving-success">
            <i class="fas fa-check-circle me-2"></i>{{ $t('Results saved successfully') }}!
          </div>
          
          <!-- Erreur de sauvegarde -->
          <div v-else-if="savingStatus === 'error'" class="saving-error">
            <i class="fas fa-exclamation-triangle me-2"></i>{{ $t('Error saving results') }}: {{ saveError }}
          </div>
        </div>
      </div>

      <!-- Competency Validation Status -->
      <div v-if="levelValidationStatus.length > 0" class="validation-section">
        <h5 class="validation-title">🎯 Competency Validation Status</h5>
        <div class="levels-grid">
          <div 
            v-for="levelStatus in levelValidationStatus" 
            :key="levelStatus.level"
            class="level-badge" 
            :class="{
              'validated': levelStatus.validated,
              'in-progress': levelStatus.correctCount > 0 && !levelStatus.validated,
              'not-started': levelStatus.correctCount === 0
            }"
          >
            <div class="level-number">{{ levelStatus.level }}</div>
            <div class="level-progress">{{ levelStatus.correctCount }}/{{ confirmationThreshold }}</div>
            <i v-if="levelStatus.validated" class="fas fa-check-circle validation-icon"></i>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Footer -->
    <Footer />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import topnavbar from './topnavbar.vue';
import Footer from '../views/footer.vue';

// Props
const props = defineProps({
  testData: Object,
  answers: Object,
  stopTimer: Boolean,
  finalAbilityLevel: Number,
  answeredQuestions: Array,
  levelValidationStatus: Array,
  confirmationThreshold: {
    type: Number,
    default: 3
  },
  savingStatus: {
    type: String,
    default: 'saving', // 'saving', 'success', 'error'
  },
  saveError: {
    type: String,
    default: ''
  }
});

// Emits
defineEmits(['time-up']);
</script>

<style scoped>
.cat-final-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.container.wrapper {
  flex: 1;
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
}

.completion-alert {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 8px;
  padding: 30px;
  margin-top: 50px;
  margin-bottom: 30px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.completion-alert h4 {
  color: #155724;
  margin-bottom: 20px;
  font-weight: 600;
}

.completion-alert p {
  color: #155724;
  margin-bottom: 10px;
  font-size: 1.1em;
}

.completion-alert p:last-of-type {
  margin-bottom: 20px;
}

.saving-indicator {
  font-size: 1em;
  padding: 15px;
  border-radius: 8px;
  display: inline-block;
  min-width: 250px;
  transition: all 0.3s ease;
}

.saving-indicator.saving {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border: 2px solid #2196f3;
  color: #1565c0;
}

.saving-indicator.saving .fa-spinner {
  animation: spin 1s linear infinite;
}

.saving-indicator.success {
  background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
  border: 2px solid #4caf50;
  color: #2e7d32;
}

.saving-indicator.error {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  border: 2px solid #f44336;
  color: #c62828;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.validation-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 25px;
  border: 2px solid #e9ecef;
}

.validation-title {
  text-align: center;
  margin-bottom: 25px;
  color: #495057;
  font-weight: 600;
}

.levels-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 15px;
  max-width: 800px;
  margin: 0 auto;
}

.level-badge {
  text-align: center;
  padding: 20px 15px;
  border-radius: 12px;
  position: relative;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  background: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.level-badge.validated {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-color: #28a745;
  box-shadow: 0 4px 8px rgba(40, 167, 69, 0.2);
}

.level-badge.in-progress {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border-color: #ffc107;
  box-shadow: 0 4px 8px rgba(255, 193, 7, 0.2);
}

.level-badge.not-started {
  background: #f8f9fa;
  border-color: #dee2e6;
}

.level-number {
  font-size: 1.4em;
  font-weight: bold;
  margin-bottom: 8px;
  color: #495057;
}

.level-badge.validated .level-number {
  color: #155724;
}

.level-badge.in-progress .level-number {
  color: #856404;
}

.level-progress {
  font-size: 0.95em;
  color: #6c757d;
  font-weight: 500;
}

.level-badge.validated .level-progress {
  color: #155724;
}

.level-badge.in-progress .level-progress {
  color: #856404;
}

.validation-icon {
  position: absolute;
  top: -8px;
  right: -8px;
  color: #28a745;
  background: white;
  border-radius: 50%;
  font-size: 1.3em;
  padding: 2px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Responsive design */
@media (max-width: 768px) {
  .container.wrapper {
    padding: 20px 15px;
  }
  
  .completion-alert {
    padding: 20px;
  }
  
  .completion-alert h4 {
    font-size: 1.3em;
  }
  
  .completion-alert p {
    font-size: 1em;
  }
  
  .levels-grid {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 10px;
  }
  
  .level-badge {
    padding: 15px 10px;
  }
  
  .level-number {
    font-size: 1.2em;
  }
}

@media (max-width: 480px) {
  .levels-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>